/*
 * File: RefundSchema.ts
 * Author: Austin Robinson
 * Date Created: 03/11/2025
 * Copyright (c) DrivingDoc 2025
 */

import { formatCents } from "utils/formatCurrency";
import { z } from "zod";

export const RefundAllocationSchema = z.object({
  // id of the payment to refund from
  id: z.string().uuid().readonly(),

  // amount to refund from the payment
  amount: z
    .number({
      required_error: "Amount is required",
      invalid_type_error: "Expected a number",
    })
    .nonnegative()
    .nullable()
    .default(null),

  /* read only fields for display/validation purposes */

  // amount previously allocated towards charge
  previous_amount: z.number().nonnegative().default(0).readonly(),

  // payment memo
  memo: z.string().readonly(),

  // total payment amount
  payment_amount: z.number().nonnegative().readonly(),

  // payment ammount allocated towards charges
  payment_amount_allocated: z.number().nonnegative().readonly(),

  // payment amount that has already been refunded
  payment_amount_refunded: z.number().nonnegative().readonly(),

  // todo, if refunding full amount offer option to deallocate payment from charges
});

export const RefundFormSchema = z.object({
  // id of the refund (null for new refunds)
  id: z.string().uuid().nullable().readonly(),

  // id of the student the refund belongs to
  student_user_id: z.string().uuid().readonly(),

  // the date the refund was received (might not be the date the refund is recorded)
  date: z
    .string({
      required_error: "Refund date is required",
    })
    .datetime()
    .default(() => new Date().toISOString()),

  /**
   * total refund amount
   * @deprecated amount is now calculated from allocations
   */
  // amount: z.number({
  //     required_error: "Amount is required",
  //     invalid_type_error: "Expected a number",
  // }).positive(),

  // memo to briefly describe the refund
  memo: z
    .string({
      required_error: "Memo is required",
    })
    .min(1),

  // longer description for the refund
  description: z.string().nullable().default(null),

  // private notes that are only visible to administrators
  private_notes: z.string().nullable().default(null),

  // id of the payment method used to issue the refund
  payment_method_id: z
    .string({ required_error: "Refund method is required" })
    .uuid(),

  // optional field to record a check number
  check_no: z.string().nullable().default(null),

  // list of payments being refunded along with the amount to refund
  allocations: z
    .array(RefundAllocationSchema)
    .default([])
    .superRefine((data, ctx) => {
      // ensure that each payment refund does not exceed the available unrefunded payment amount ("payment_amount - payment_amount_refunded")
      data.forEach((allocation, index) => {
        // get the total payment amount
        const paymentAmount = allocation.payment_amount;

        // get the amount allocated towards charges
        const paymentAmountAllocated = allocation.payment_amount_allocated;

        // get the amount that has already been refunded
        const paymentAmountRefunded = allocation.payment_amount_refunded;

        // calculate the amount that can be refunded, accounting for any previous amount being replaced
        const refundableAmount =
          paymentAmount -
          paymentAmountAllocated -
          paymentAmountRefunded +
          (allocation.previous_amount ?? 0);

        // new amount to refund from the payment
        const requested = allocation.amount ?? 0;

        // if the requested refund exceeds the max refundable amount
        if (requested > refundableAmount) {
          // add an issue to the invalid field
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [index, "amount"],
            message: `Amount cannot exceed the refundable amount (${formatCents(refundableAmount)}) for this payment.`,
          });
        }
      });
    }),
});

export type RefundFormValues = z.infer<typeof RefundFormSchema>;
